import { useMemo } from "react";
import { ConstraintOperator } from "models/constraints";
import { TooltipHost, Spinner, SpinnerSize } from "@fluentui/react";
import {
  useGetPolicyConstraintsQuery,
  useGetPolicyConstraintValuesQuery,
} from "services/template";

export interface IPolicyCriteria {
  id: string;
  name: string;
  operator: ConstraintOperator;
  values: string[];
  keyId?: string;
  keyDisplay?: string;
  valueDisplay?: string;
}

/**
 * Renders a single criterion row, calling the constraint values API
 * to resolve display-friendly value names.
 */
function PolicyCriterionRow({
  criterion,
  index,
  constraintDisplayName,
  constraintApiId,
}: {
  criterion: IPolicyCriteria;
  index: number;
  constraintDisplayName: string;
  constraintApiId: string | undefined;
}) {
  // Fetch constraint values to resolve display names for each value
  const { data: constraintValues = [], isLoading: isLoadingValues } = useGetPolicyConstraintValuesQuery(
    constraintApiId!,
    { skip: !constraintApiId }
  );

  // Build a lookup map: value name/id/key -> display name
  // Priority: show display first, if no display then show name
  const valueLookup = useMemo(() => {
    const map: Record<string, string> = {};
    constraintValues.forEach((v: any) => {
      const displayText = v.display || v.name || v.key || "";
      // Index by name, id, and key so lookup matches regardless of what's stored
      if (v.name) map[v.name] = displayText;
      if (v.id) map[v.id] = displayText;
      if (v.key && v.key !== v.name && v.key !== v.id) map[v.key] = displayText;
    });
    return map;
  }, [constraintValues]);

  // Resolve display text for values using the API lookup
  const resolvedValuesDisplay = useMemo(() => {
    if (!criterion.values || criterion.values.length === 0) return "";
    const displayNames = criterion.values.map(
      (val) => valueLookup[val] || val
    );
    return displayNames
      .sort()
      .join(", ")
      .replace(/, ([^,]*)$/, " or $1");
  }, [criterion.values, valueLookup]);

  return (
    <div
      key={`criterion_${criterion.id}_${index}`}
      style={{
        display: "flex",
        flexDirection: "row",
        alignItems: "flex-start",
        padding: 0,
        width: "100%",
        flex: "none",
        order: index,
        flexGrow: 0,
      }}
    >
      {/* Attribute name */}
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "flex-start",
          padding: 0,
          gap: 10,
          width: 147,
          minHeight: 16,
          flex: "none",
          order: 0,
          flexGrow: 0,
        }}
      >
        {constraintDisplayName.length > 20 ? (
          <TooltipHost content={constraintDisplayName}>
            <div
              style={{
                width: 147,
                fontFamily: "Segoe UI",
                fontStyle: "normal",
                fontWeight: 400,
                fontSize: 12,
                lineHeight: "16px",
                color: "#605E5C",
                flex: "none",
                order: 0,
                flexGrow: 0,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {constraintDisplayName}
            </div>
          </TooltipHost>
        ) : (
          <div
            style={{
              width: 147,
              fontFamily: "Segoe UI",
              fontStyle: "normal",
              fontWeight: 400,
              fontSize: 12,
              lineHeight: "16px",
              color: "#605E5C",
              flex: "none",
              order: 0,
              flexGrow: 0,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {constraintDisplayName}
          </div>
        )}
      </div>

      {/* Operator */}
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          alignItems: "center",
          padding: 0,
          gap: 10,
          width: 24,
          minHeight: 16,
          flex: "none",
          order: 1,
          flexGrow: 0,
        }}
      >
        <div
          style={{
            fontFamily: "Segoe UI",
            fontStyle: "normal",
            fontWeight: 400,
            fontSize: 12,
            lineHeight: "16px",
            textAlign: "center",
            color: "#605E5C",
            flex: "none",
            order: 0,
            flexGrow: 0,
          }}
        >
          {criterion.operator === ConstraintOperator.Include ? "=" : "!="}
        </div>
      </div>

      {/* Values */}
      <div
        style={{
          fontFamily: "Segoe UI",
          fontStyle: "normal",
          fontWeight: 400,
          fontSize: 12,
          lineHeight: "16px",
          color: "#605E5C",
          order: 2,
          flexGrow: 1,
          flexShrink: 1,
          minWidth: 0,
          overflowWrap: "break-word",
          wordBreak: "break-word",
        }}
      >
        {constraintApiId && isLoadingValues ? (
          <Spinner size={SpinnerSize.xSmall} />
        ) : (
          resolvedValuesDisplay
        )}
      </div>
    </div>
  );
}

export function PolicyCriteriaDisplay({
  criteria,
  allConstraints,
}: {
  criteria: IPolicyCriteria[];
  allConstraints: any[];
}) {
  // Build a lookup: constraint name/id/key -> { display, apiId }
  const constraintLookup = useMemo(() => {
    const map: Record<string, { display: string; apiId: string }> = {};
    allConstraints.forEach((c: any) => {
      const displayName = c.display || c.name || c.key || "";
      const apiId = c.id || c.key || "";
      const entry = { display: displayName, apiId };
      // Index by name, id, and key so lookup never misses
      if (c.name) map[c.name] = entry;
      if (c.id) map[c.id] = entry;
      if (c.key && c.key !== c.name && c.key !== c.id) map[c.key] = entry;
    });
    return map;
  }, [allConstraints]);

  if (!criteria || criteria.length === 0) {
    return null;
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        padding: 0,
        gap: 6,
        width: "100%",
      }}
    >
      {criteria.map((criterion, index) => {
        const lookup = constraintLookup[criterion.name] || constraintLookup[criterion.keyId || ""];
        const constraintDisplayName = lookup?.display || criterion.keyDisplay || criterion.name;
        const constraintApiId = lookup?.apiId || criterion.keyId;
        return (
          <PolicyCriterionRow
            key={`criterion_${criterion.id}_${index}`}
            criterion={criterion}
            index={index}
            constraintDisplayName={constraintDisplayName}
            constraintApiId={constraintApiId}
          />
        );
      })}
    </div>
  );
}

function PolicyCriteriaDisplayWrapper({
  criteria,
}: {
  criteria: IPolicyCriteria[];
}) {
  // Fetch all constraints to resolve display-friendly key names
  const { data: allConstraints = [], isLoading: isLoadingConstraints } = useGetPolicyConstraintsQuery();

  // Show spinner while constraints API is loading
  if (isLoadingConstraints) {
    return (
      <div style={{ display: "flex", width: "100%", alignItems: "center" }}>
        <Spinner size={SpinnerSize.xSmall} />
      </div>
    );
  }

  return (
    <div style={{ display: "flex", width: "100%" }}>
      <PolicyCriteriaDisplay criteria={criteria} allConstraints={allConstraints} />
    </div>
  );
}

export default PolicyCriteriaDisplayWrapper;